import { useNavigate } from "react-router-dom";
import { Clock, RefreshCw, Ban } from "lucide-react";

import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { AuthButton } from "@/features/auth/components/AuthButton";
import { useRefreshStatus } from "@/features/auth/api/hooks";
import { useAuthStore } from "@/features/auth/store";

/** Shown to a logged-in investor/loaner who can't chat yet — either waiting for admin
 *  approval (pending) or whose access has been deactivated (suspended). */
export function PendingApproval() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const setStatus = useAuthStore((s) => s.setStatus);
  const logout = useAuthStore((s) => s.logout);
  const refresh = useRefreshStatus();

  const suspended = status === "suspended";

  const recheck = () =>
    refresh.mutate(undefined, {
      onSuccess: ({ status }) => setStatus(status),
    });

  return (
    <AuthLayout>
      <div className="space-y-6 text-center">
        <div
          className={`mx-auto flex size-16 items-center justify-center rounded-full ${
            suspended ? "bg-red-400/15" : "bg-amber-400/15"
          }`}
        >
          {suspended ? (
            <Ban className="size-8 text-red-400" />
          ) : (
            <Clock className="size-8 text-amber-400" />
          )}
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">
            {suspended ? "Tilgang deaktivert" : "Venter på godkjenning"}
          </h2>
          <p className="text-sm text-white/80">
            {suspended
              ? "Tilgangen din til Oblinor-chat er deaktivert. Ta kontakt med Oblinor på post@oblinor.no hvis du mener dette er en feil."
              : "Takk! E-posten din er bekreftet. En administrator må godkjenne tilgangen din før du kan starte samtaler. Du får en e-post så snart kontoen er aktivert."}
          </p>
        </div>

        {!suspended && refresh.isSuccess && refresh.data.status !== "active" && (
          <p className="text-sm text-amber-300">Fortsatt under behandling — prøv igjen senere.</p>
        )}

        <div className="flex flex-col gap-3">
          {!suspended && (
            <AuthButton type="button" onClick={recheck} loading={refresh.isPending}>
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="size-4" /> Sjekk status på nytt
              </span>
            </AuthButton>
          )}
          <AuthButton
            type="button"
            variant="outline"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
          >
            Logg ut
          </AuthButton>
        </div>
      </div>
    </AuthLayout>
  );
}
